import { ROLES } from "./roles";

const { ADMIN, AUDITOR, USER, SETUP_ADMIN, SUPERVISOR } = ROLES;

// Permission keys — reference these constants instead of raw strings so that
// typos surface as `undefined` lookups rather than silently passing.
export const PERMISSION = {
  // ── SASSP (Asset Scan) › Asset Add ──────────────────────────────────────
  INITIATE_SCAN: "INITIATE_SCAN",
  VIEW_SCAN_RESULTS: "VIEW_SCAN_RESULTS",
  DOWNLOAD_SCAN_REPORT: "DOWNLOAD_SCAN_REPORT",
  ADD_IMPORT_ASSET: "ADD_IMPORT_ASSET",
  EDIT_ASSET: "EDIT_ASSET",
  VIEW_ASSET_REQUEST: "VIEW_ASSET_REQUEST",
  // Onboard and Approval are two pages, and Supervisor may see only the second
  // of them, so each gates on its own key rather than sharing one.
  VIEW_ASSET_APPROVAL: "VIEW_ASSET_APPROVAL",
  APPROVE_REJECT_ASSET: "APPROVE_REJECT_ASSET",
  STOP_SCAN: "STOP_SCAN",

  // ── Administration › User Management ────────────────────────────────────
  VIEW_USERS: "VIEW_USERS",
  DISABLE_USERS: "DISABLE_USERS",

  // ── Administration › User Management › Role Mapping ─────────────────────
  VIEW_AD_GROUPS: "VIEW_AD_GROUPS",
  ADD_GROUP_NAME: "ADD_GROUP_NAME",
  EDIT_GROUP_NAME: "EDIT_GROUP_NAME",

  // ── Administration › Tools ──────────────────────────────────────────────
  VIEW_TOOLS_LIST: "VIEW_TOOLS_LIST",
  ADD_EDIT_TOOLS: "ADD_EDIT_TOOLS",
  DELETE_TOOLS: "DELETE_TOOLS",

  // ── Administration ›    ─────────────────────────────────────────
  VIEW_EMAIL_SCHEDULER: "VIEW_EMAIL_SCHEDULER",
  ENABLE_DISABLE_EMAIL_ALERT: "ENABLE_DISABLE_EMAIL_ALERT",
  ADD_EMAIL_SCHEDULER: "ADD_EMAIL_SCHEDULER",
  EDIT_EMAIL_SCHEDULER: "EDIT_EMAIL_SCHEDULER",
  DELETE_EMAIL_SCHEDULER: "DELETE_EMAIL_SCHEDULER",

  // ── Settings ────────────────────────────────────────────────────────────
  // The AD/LDAP, Licence and SMTP config pages are edit-only forms with no
  // read-only view, so a single "manage" permission gates both page access
  // and the add/edit/save actions.
  MANAGE_AD_CONFIG: "MANAGE_AD_CONFIG",
  MANAGE_LICENSE_CONFIG: "MANAGE_LICENSE_CONFIG",
  MANAGE_SMTP_CONFIG: "MANAGE_SMTP_CONFIG",
  EDIT_LOGIN_BANNER: "EDIT_LOGIN_BANNER",

  // ── Audit Log ───────────────────────────────────────────────────────────
  VIEW_AUDIT_LOG: "VIEW_AUDIT_LOG",
  EXPORT_AUDIT_LOG: "EXPORT_AUDIT_LOG",

  // ── User Session ────────────────────────────────────────────────────────
  VIEW_USER_SESSION: "VIEW_USER_SESSION",
};

// Each permission maps to the roles allowed to perform it.
//
// Source of truth: the SASSP (Asset Scan) and Administration RBAC matrices.
// Row-level qualifiers ("Own request", "reporting users", "Workflow") are
// enforced by the backend; the frontend only gates on role here. Extend this
// map as more modules/pages are brought under RBAC.
export const ROLE_PERMISSIONS = {
  // SASSP (Asset Scan)
  [PERMISSION.INITIATE_SCAN]: [ADMIN, USER],
  [PERMISSION.VIEW_SCAN_RESULTS]: [ADMIN, AUDITOR, USER],
  [PERMISSION.DOWNLOAD_SCAN_REPORT]: [ADMIN, USER],
  [PERMISSION.ADD_IMPORT_ASSET]: [ADMIN, USER],
  [PERMISSION.EDIT_ASSET]: [ADMIN, USER],
  [PERMISSION.VIEW_ASSET_REQUEST]: [ADMIN, AUDITOR, USER],
  // Everyone who can reach the page can read it; deciding on a request is the
  // Admin's and the Supervisor's. Auditor and User raise requests and watch
  // them, so they get the list without the checkboxes or the action buttons.
  [PERMISSION.VIEW_ASSET_APPROVAL]: [ADMIN, AUDITOR, USER, SUPERVISOR],
  [PERMISSION.APPROVE_REJECT_ASSET]: [ADMIN, SUPERVISOR],
  [PERMISSION.STOP_SCAN]: [ADMIN],

  // Administration › User Management
  [PERMISSION.VIEW_USERS]: [ADMIN, SETUP_ADMIN],
  [PERMISSION.DISABLE_USERS]: [ADMIN, SETUP_ADMIN],

  // Administration › Role Mapping
  [PERMISSION.VIEW_AD_GROUPS]: [ADMIN, SETUP_ADMIN],
  [PERMISSION.ADD_GROUP_NAME]: [ADMIN, SETUP_ADMIN],
  [PERMISSION.EDIT_GROUP_NAME]: [ADMIN, SETUP_ADMIN],

  // Administration › Tools
  // Setup Admin has the same rights as Admin on this page.
  [PERMISSION.VIEW_TOOLS_LIST]: [ADMIN, SETUP_ADMIN],
  [PERMISSION.ADD_EDIT_TOOLS]: [ADMIN, SETUP_ADMIN],
  [PERMISSION.DELETE_TOOLS]: [ADMIN, SETUP_ADMIN],

  // Administration › Schedulers
  [PERMISSION.VIEW_EMAIL_SCHEDULER]: [ADMIN],
  [PERMISSION.ENABLE_DISABLE_EMAIL_ALERT]: [ADMIN],
  [PERMISSION.ADD_EMAIL_SCHEDULER]: [ADMIN],
  [PERMISSION.EDIT_EMAIL_SCHEDULER]: [ADMIN],
  [PERMISSION.DELETE_EMAIL_SCHEDULER]: [ADMIN],

  // Settings
  [PERMISSION.MANAGE_AD_CONFIG]: [ADMIN, SETUP_ADMIN],
  [PERMISSION.MANAGE_LICENSE_CONFIG]: [ADMIN, SETUP_ADMIN],
  [PERMISSION.MANAGE_SMTP_CONFIG]: [ADMIN, SETUP_ADMIN],
  [PERMISSION.EDIT_LOGIN_BANNER]: [ADMIN],

  // Audit Log
  [PERMISSION.VIEW_AUDIT_LOG]: [ADMIN, AUDITOR],
  [PERMISSION.EXPORT_AUDIT_LOG]: [ADMIN],

  // User Session
  // The page carried no permission at all, which made it the one page every
  // role could reach. Named here so it fails closed like the rest.
  [PERMISSION.VIEW_USER_SESSION]: [ADMIN, AUDITOR],
};

// Normalise `userRole` to a string array. It arrives as an array (e.g.
// ["Admin"]) once login resolves, or as an empty string from the store's
// initial state.
export const normalizeRoles = (userRole) => {
  if (Array.isArray(userRole)) {
    return userRole.filter(Boolean);
  }
  if (typeof userRole === "string" && userRole.trim()) {
    return [userRole.trim()];
  }
  return [];
};

// Pure role check usable outside React (e.g. in route config). An unknown
// permission key resolves to `false` so missing entries fail closed.
export const roleCan = (userRole, permissionKey) => {
  const allowedRoles = ROLE_PERMISSIONS[permissionKey];
  if (!allowedRoles) {
    return false;
  }
  const roles = normalizeRoles(userRole);
  return roles.some((role) => allowedRoles.includes(role));
};

// Whether the user may view a single route. Routes without a `permission`
// field are unrestricted (visible to everyone) — only opt routes into RBAC by
// adding `permission`.
export const roleCanViewRoute = (userRole, route) => {
  if (!route || !route.permission) {
    return true;
  }
  return roleCan(userRole, route.permission);
};

// Whether a route should appear at all. A parent (has childNavs) is visible
// when at least one descendant is visible; a leaf is visible per its own
// `permission`. This drives both the sidebar and the URL guard.
export const isRouteVisible = (userRole, route) => {
  if (!route) {
    return false;
  }
  if (route.childNavs && route.childNavs.length) {
    return route.childNavs.some((child) => isRouteVisible(userRole, child));
  }
  return roleCanViewRoute(userRole, route);
};
